import { useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  orderBy,
  limit,
  startAfter,
  where,
  or,
} from "firebase/firestore";

import { db } from "../services/firebase";
import { useAuth } from "../services/authService";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

import { sendApprovalEmail } from "../services/EmailService";

import "./ServiceAdminUsers.scss";

function normalizeStatus(status) {
  if (!status) return "pending";
  return status === "rejected" ? "denied" : status;
}

function formatCreatedAt(createdAt) {
  if (!createdAt) {
    return { date: "—", time: "", value: 0 };
  }

  try {
    const dateObj = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);

    return {
      date: dateObj.toLocaleDateString("cs-CZ"),
      time: dateObj.toLocaleTimeString("cs-CZ"),
      value: dateObj.getTime(),
    };
  } catch {
    return { date: "—", time: "", value: 0 };
  }
}

function isAdminAccount(data) {
  const role = String(data?.role || "").trim();
  const adminRole = String(data?.adminRole || "").trim();

  return (
    [
      "admin",
      "super_admin",
      "superadmin",
      "service_admin",
      "client_admin",
      "firmware_admin",
    ].includes(adminRole) ||
    ["admin", "super_admin", "service_admin", "client_admin"].includes(role)
  );
}

export default function ServiceAdminUsers() {
  const PAGE_SIZE = 5;

  const { currentUser } = useAuth();

  const topRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [totalVisibleUsers, setTotalVisibleUsers] = useState(null);

  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([]);
  const [hasNext, setHasNext] = useState(false);

  const mapUserDoc = (d) => {
    const raw = d.data() || {};

    const fullName = `${raw.name || ""} ${raw.surname || ""}`.trim() || "—";

    const { date, time, value } = formatCreatedAt(raw.createdAt);

    return {
      id: d.id,
      name: fullName,
      email: raw.email || "—",
      phone: raw.phone || "—",
      company: raw.company || "—",
      contactPerson: raw.contactPerson || "—",
      status: normalizeStatus(raw.status),
      createdAtDate: date,
      createdAtTime: time,
      createdAtValue: value,
      role: raw.role || null,
      adminRole: raw.adminRole || null,
      requestedRole: raw.requestedRole || "service",
      isAdmin: isAdminAccount(raw),
      doc: d,
    };
  };

  const fetchVisibleUserCount = async () => {
    try {
      const usersRef = collection(db, "users");

      const snap = await getDocs(
        query(
          usersRef,
          or(
            where("role", "==", "service"),
            where("requestedRole", "==", "service"),
          ),
        ),
      );

      const mapped = snap.docs.map(mapUserDoc);

      setTotalVisibleUsers(mapped.length);
    } catch (e) {
      console.error(e);
      setTotalVisibleUsers(null);
    }
  };

  const fetchPage = async (targetPage = 1) => {
    setLoading(true);
    setError("");

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const usersRef = collection(db, "users");

      const cursor =
        targetPage <= 1 ? null : pageCursors[targetPage - 2] || null;

      const constraints = [];

      constraints.push(
        or(
          where("role", "==", "service"),
          where("requestedRole", "==", "service"),
        ),
      );

      constraints.push(orderBy("createdAt", "desc"));

      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      constraints.push(limit(PAGE_SIZE + 1));

      const snap = await getDocs(query(usersRef, ...constraints));

      const docs = snap.docs || [];
      const docsForPage = docs.slice(0, PAGE_SIZE);

      setHasNext(docs.length > PAGE_SIZE);

      const lastDoc = docsForPage.length
        ? docsForPage[docsForPage.length - 1]
        : null;

      setPageCursors((prev) => {
        const next = [...prev];
        next[targetPage - 1] = lastDoc;
        return next;
      });

      const list = docsForPage
        .map(mapUserDoc)
        .sort((a, b) => b.createdAtValue - a.createdAtValue);

      setUsers(list);
      setPage(targetPage);
    } catch (e) {
      console.error(e);

      setError("Nepodařilo se načíst servisní techniky z Firestore.");

      setUsers([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(1);
    fetchVisibleUserCount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((u) => {
      const hay = [
        u.name,
        u.email,
        u.phone,
        u.company,
        u.contactPerson,
        u.status,
        u.role,
        u.adminRole,
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [users, search]);

  const approveUser = async (user) => {
    if (user.isAdmin) {
      alert("Admin účet nelze z tohoto seznamu schvalovat.");
      return;
    }

    setBusyId(user.id);
    setError("");

    try {
      const ref = doc(db, "users", user.id);

      await updateDoc(ref, {
        status: "approved",
        role: "service",
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, status: "approved", role: "service" } : u,
        ),
      );

      if (user.email && user.name) {
        await sendApprovalEmail({
          to_email: user.email,
          to_name: user.name,
        });
      }
    } catch (e) {
      console.error(e);

      setError(
        "Schválení se nezdařilo. Zkontroluj Firestore rules / oprávnění service_admin.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const denyUser = async (user) => {
    if (user.isAdmin) {
      alert("Admin účet nelze z tohoto seznamu zamítnout.");
      return;
    }

    setBusyId(user.id);
    setError("");

    try {
      const ref = doc(db, "users", user.id);

      await updateDoc(ref, { status: "denied" });

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, status: "denied" } : u)),
      );
    } catch (e) {
      console.error(e);

      setError(
        "Zamítnutí se nezdařilo. Zkontroluj Firestore rules / oprávnění service_admin.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (user) => {
    if (currentUser?.uid === user.id) {
      alert("Nemůžete smazat sám sebe.");
      return;
    }

    if (user.isAdmin) {
      alert("Admin účet nelze z tohoto seznamu smazat.");
      return;
    }

    const ok = window.confirm("Opravdu chcete uživatele trvale smazat?");

    if (!ok) return;

    setBusyId(user.id);
    setError("");

    try {
      await deleteDoc(doc(db, "users", user.id));

      await fetchPage(page);
      await fetchVisibleUserCount();
    } catch (e) {
      console.error(e);

      setError(
        "Nepodařilo se smazat uživatele. Zkontroluj Firestore rules / oprávnění service_admin.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const getRoleLabel = (user) => {
    if (user.isAdmin && user.adminRole === "service_admin") {
      return "Servisní admin";
    }

    if (
      user.isAdmin &&
      (user.adminRole === "admin" || user.adminRole === "super_admin")
    ) {
      return "Super admin";
    }

    return "Servisní technik";
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "approved":
      case "active":
        return (
          <>
            <span className="status-icon approved" title="Schválen">
              <FaCheckCircle />
            </span>

            <span className="status-text">Schválen</span>
          </>
        );

      case "pending":
        return (
          <>
            <span className="status-icon pending" title="Čeká na schválení">
              <FaHourglassHalf />
            </span>

            <span className="status-text">Čeká</span>
          </>
        );

      case "denied":
        return (
          <>
            <span className="status-icon rejected" title="Zamítnut">
              <FaTimesCircle />
            </span>

            <span className="status-text">Zamítnut</span>
          </>
        );

      default:
        return <span>—</span>;
    }
  };

  const renderActions = (user) => {
    const isBusy = busyId === user.id;

    if (user.isAdmin) {
      return <span className="admin-protected">Admin účet</span>;
    }

    return (
      <div className="action-buttons">
        {(user.status === "pending" || user.status === "denied") && (
          <button
            className="approve pulse-approve"
            disabled={isBusy}
            onClick={() => approveUser(user)}
          >
            {isBusy ? "..." : "Schválit"}
          </button>
        )}

        {(user.status === "pending" ||
          user.status === "approved" ||
          user.status === "active") && (
          <button
            className="deny"
            disabled={isBusy}
            onClick={() => denyUser(user)}
          >
            {isBusy ? "..." : "Zamítnout"}
          </button>
        )}

        <button
          className="delete"
          disabled={isBusy}
          onClick={() => deleteUser(user)}
        >
          {isBusy ? "..." : "Smazat"}
        </button>
      </div>
    );
  };

  const goPrev = () => {
    if (page <= 1) return;
    fetchPage(page - 1);
  };

  const goNext = () => {
    if (!hasNext) return;
    fetchPage(page + 1);
  };

  return (
    <div className="service-admin-users-page" ref={topRef}>
      <h1>
        Seznam registrovaných uživatelů
        {typeof totalVisibleUsers === "number" && (
          <span className="heading-count">({totalVisibleUsers})</span>
        )}
      </h1>

      <p className="mode-line">Režim: service_admin</p>

      <div className="admin-search">
        <label htmlFor="adminSearch">Hledat:</label>

        <input
          id="adminSearch"
          type="text"
          placeholder="jméno / email / firma / tel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="error">{error}</p>}

      <div className="table-content-wrapper">
        {loading ? (
          <p>Načítání uživatelů...</p>
        ) : filteredUsers.length === 0 ? (
          <p>Žádní servisní technici nenalezeni.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Jméno:</th>
                <th>Email:</th>
                <th>Telefon:</th>
                <th>Společnost:</th>
                <th>Ověřovací kontakt:</th>
                <th>Typ účtu:</th>
                <th>Stav:</th>
                <th>Registrován:</th>
                <th>Akce:</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <span className="label">Jméno:</span>
                    <span className="value">{user.name}</span>
                  </td>

                  <td>
                    <span className="label">Email:</span>
                    <span className="value">{user.email}</span>
                  </td>

                  <td>
                    <span className="label">Telefon:</span>
                    <span className="value">{user.phone}</span>
                  </td>

                  <td>
                    <span className="label">Společnost:</span>
                    <span className="value">{user.company}</span>
                  </td>

                  <td>
                    <span className="label">Ověřovací kontakt:</span>
                    <span className="value">{user.contactPerson}</span>
                  </td>

                  <td>
                    <span className="label">Typ účtu:</span>
                    <span className="value">{getRoleLabel(user)}</span>
                  </td>

                  <td>
                    <span className="label">Stav:</span>

                    <span className="value status-cell">
                      {getStatusDisplay(user.status)}
                    </span>
                  </td>

                  <td>
                    <span className="label">Registrován:</span>

                    <span className="value registered">
                      <span className="date">{user.createdAtDate}</span>{" "}
                      <span className="time">{user.createdAtTime}</span>
                    </span>
                  </td>

                  <td>
                    <span className="label">Akce:</span>

                    <span className="value">{renderActions(user)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(users.length > 0 || page > 1) && !loading && (
        <div className="pagination-wrapper">
          <button
            className="pagination-btn"
            onClick={goPrev}
            disabled={page <= 1 || loading}
          >
            <FaArrowLeft className="icon" />
            <span className="text">Předchozí</span>
          </button>

          <div className="page-info">
            Stránka <b>{page}</b>
          </div>

          <button
            className="pagination-btn"
            onClick={goNext}
            disabled={!hasNext || loading}
          >
            <FaArrowRight className="icon" />
            <span className="text">Další</span>
          </button>
        </div>
      )}
    </div>
  );
}
